import { Pagination as CarbonPagination, PaginationProps } from '@carbon/react';
import React, { useMemo } from 'react';
import { format0 } from 'utils/number';
import style from './pagination.module.scss';

// Carbon's Pagination creates a <select> with an <option> for every page.
// With large datasets (e.g., 6M documents / 100 per page = 66K pages),
// this freezes the browser. The v10 workaround was class inheritance to
// override renderSelectItems with a ±9 page window — not possible in v11
// (functional component).
//
// Solution: always pass pagesUnknown={true} to skip Carbon's <Select> entirely,
// then render our own <select> with a ±9 page window (max 19 options).
export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (props, ref) => {
    const totalPages =
      props.totalItems && props.pageSize
        ? Math.ceil(props.totalItems / props.pageSize)
        : 0;

    const currentPage = props.page || 1;

    const pageOptions = useMemo(() => {
      const firstPage = Math.max(1, currentPage - 9);
      const lastPage = Math.min(totalPages, currentPage + 9);
      const options = [];
      for (let i = firstPage; i <= lastPage; i++) {
        options.push(i);
      }
      return options;
    }, [currentPage, totalPages]);

    const handlePageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const page = parseInt(e.target.value, 10);
      if (!isNaN(page)) {
        props.onChange?.({ page, pageSize: props.pageSize || 10 });
      }
    };

    // pagesUnknown uses itemText(min, max) instead of itemRangeText(min, max, total).
    // Two issues: (1) pagesUnknown doesn't clamp max to totalItems, so last page
    // shows "1–100" instead of "1–50" when only 50 items exist. (2) totalItems not shown.
    // Bridge: clamp max, then delegate to consumer's itemRangeText if provided.
    const totalItems = props.totalItems || 0;
    const itemText = (min: number, max: number) => {
      const clampedMax = Math.min(max, totalItems);
      if (props.itemRangeText) {
        return props.itemRangeText(min, clampedMax, totalItems);
      }
      return `${format0(min)}\u2013${format0(clampedMax)} of ${format0(
        totalItems
      )} items`;
    };

    return (
      <div className={style.paginationWrapper}>
        <CarbonPagination
          ref={ref}
          {...props}
          pagesUnknown={true}
          itemText={itemText}
          isLastPage={totalPages > 0 && currentPage >= totalPages}
        />
        {totalPages > 0 && (
          <div className={style.pageSelect}>
            <select
              className={style.pageSelectField}
              value={currentPage}
              onChange={handlePageChange}
              aria-label={`Page number, of ${totalPages} pages`}
            >
              {pageOptions.map((page) => (
                <option key={page} value={page}>
                  {page}
                </option>
              ))}
            </select>
            <span className={style.pageSelectLabel}>
              {props.pageRangeText
                ? props.pageRangeText(currentPage, totalPages)
                : `of ${format0(totalPages)} ${
                    totalPages === 1 ? 'page' : 'pages'
                  }`}
            </span>
          </div>
        )}
      </div>
    );
  }
);

Pagination.displayName = 'Pagination';
